import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Box, FormControlLabel, Radio } from "@mui/material";

import DXIcon from "features/common/components/DXIcon";
import DXModal from "features/common/components/DXModal";
import QBButton from "features/common/components/QBButton/QBButton";
import QBLogoutButton from "features/common/components/QBButton/QBLogoutButton";
import { Step } from "features/common/components/Step/Step";
import useConnectQuickbooks from "hooks/useConnectQuickbooks";
import useCurrentUser from "hooks/useCurrentUser";
import Dropzone, { IFileWithMeta } from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { RootState } from "redux/reducers";
import { post } from "services/api";
import { common } from "theme/common";
import * as Styled from "../../common/components/DragAndDrop/DragAndDrop.styles";
import {
  ButtonContainer,
  DragAndDropContainer,
  Input,
  QBStep,
  StepContainerColumn
} from "./Steps.styles";
import { QBLogoutModal } from "features/common/components/QBLogoutModal/QBLogoutModal";
import ModalAddExistingQBAccount from "features/common/components/AddExistingQBAccount/ModalAddExistingQBAccount";

interface Step2Props {
  accountId: string;
  companyId: string;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
}

interface IFilesToUpload {
  name: string;
  size: number;
  status: string;
}

export const Step2 = ({ accountId, companyId, setIsUploading }: Step2Props) => {
  const { t } = useTranslation();
  const [batchJobId, setBatchJobId] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState<IFilesToUpload[]>([]);
  const [uploadsDone, setUploadsDone] = useState(false);
  const { currentCompany } = useSelector((state: RootState) => state);
  const { userData, dxUserData } = useCurrentUser();
  const params = new URLSearchParams(window.location.search);
  const isReconnect = params.get("connected") === "false";
  const [isQBconnected, setQBconnected] = useState(false)
  const [isExistingQB, setIsExistingQB] = useState(false)
  const [confirmLogoutModal, setConfirmLogoutModal] = useState(false)

  const getUploadParams = async ({ file, meta }) => {
    try {
      const response = await post("/files/v2/files", {
        original_filename: file.name,
        metadata: meta,
        origin: "quickbooks",
        content_type: file.type,
        membership_account_id: accountId,
        membership_company_id: companyId,
        batch_job_id: batchJobId,
      });

      return { body: file, url: response.preSignedUrl, meta, method: "put" };
    } catch (error) {
      console.error("Failed to upload file", error);
    }
  };

  const handleChangeStatus = (file: IFileWithMeta, status) => {
    setIsUploading(false);
    if (status === "preparing") {
      setFilesToUpload((prev) => {
        prev.push({
          name: file.meta.name,
          status: status,
          size: file.meta.size,
        });

        return prev;
      });
      setUploadsDone(false);
    }

    if (status === "error_upload") {
      toast.error(`Failed to upload file ${file.meta.name}!`);
    }

    if (status === "done") {
      const updated: IFilesToUpload[] = filesToUpload.map((f) => {
        if (f.name === file.meta.name && f.size === file.meta.size) {
          return { ...f, status: status };
        }
        return f;
      });
      setIsUploading(true);
      setFilesToUpload(updated);
    }
  };

  useEffect(() => {
    if (filesToUpload.length > 0)
      setUploadsDone(filesToUpload.every((f) => f.status === "done"));
  }, [filesToUpload]);

  useEffect(() => {
    setBatchJobId(uuidv4());
  }, []);

  const handleJobs = async () => {
    try {
      await post("/jobs/v2/jobs", {
        origin: "quickbooks",
        metadata: {},
        membership_account_id: accountId,
        membership_company_id: companyId,
        batch_job_id: batchJobId,
      });
      window.location.reload();
    } catch (error) {
      console.error("Failed to upload file", error);
    }
  };

  const handleLogoutModal = () => {
    setConfirmLogoutModal(true)
  }

  const handleQBconnection = async () => {
    setConfirmLogoutModal(false);
    setIsExistingQB(true)
  }

  useEffect(() => {
    if (dxUserData?.accounts?.length) {
      setQBconnected(Boolean(currentCompany?.account?.companies.find(el => el.id === currentCompany?.company?.id)?.credentials_id))
    }
  }, [dxUserData, currentCompany])

  return (
    <StepContainerColumn>
      <Step
        title="Step 2"
        color={common.colors.blueDark}
        text={t("upload-files-step2")}
        isUpload
      />
      <DragAndDropContainer>
        <Dropzone
          disabled={!isQBconnected}
          onChangeStatus={handleChangeStatus}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          getUploadParams={getUploadParams}
          inputContent={
            <Styled.Information>
              <Styled.IconContainer>
                <Styled.IconUpload iconName="upload-icon" />
              </Styled.IconContainer>
              <Styled.ColumnText>
                <Styled.RowText>
                  <Styled.Text className="upload">
                    {t("step-Organization-Upload-Button1")}
                  </Styled.Text>
                  <Styled.Text>
                    {t("step-Organization-Upload-Button2")}
                  </Styled.Text>
                </Styled.RowText>
                <Styled.Text>
                  {t("step-Organization-Upload-Button-Information-Sheet")}
                </Styled.Text>
              </Styled.ColumnText>
            </Styled.Information>
          }
          styles={{
            dropzone: {
              border: "1px dashed #bdb4fe",
              height: "220px",
            },
            inputLabel: {
              paddingTop: "60px",
            },
            dropzoneActive: {
              background: "#d6d1fa",
              outline: "#bdb4fe",
            },
          }}
        />
      </DragAndDropContainer>
      <ButtonContainer>
        {isQBconnected ?
          <QBButton
            children={t("send-files-quickbooks")}
            disabled={!uploadsDone}
            onClick={handleJobs}
          />
          :
          <QBButton
            children={t("quickbooks-button")}
            onClick={handleLogoutModal}
          />
        }
      </ButtonContainer>
      {confirmLogoutModal && (
        <QBLogoutModal 
          openModal={confirmLogoutModal}
          onClose={() => setConfirmLogoutModal(false)}
          logoutConfirmed={handleQBconnection}
          className="clientAccount"
        />
      )}
      {isExistingQB && (
        <ModalAddExistingQBAccount 
          openModal={isExistingQB}
          onClose={() => setIsExistingQB(false)}
          accountId={accountId}
          companyId={companyId}
          cognitoId={userData?.sub}
        />
      )}
    </StepContainerColumn>
  );
};
